import { Link, usePage } from '@inertiajs/react'
import { LogoIcon } from './Logo'
import { FuturaLogo } from './FuturaLogo'

interface FooterLinkItem {
  label: string
  href: string
}

const productLinks: FooterLinkItem[] = [
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'API Docs', href: '/docs' },
  { label: 'API Explorer', href: '/explorer' },
  { label: 'Status', href: '/status' },
]

const developerLinks: FooterLinkItem[] = [
  { label: 'llms.txt', href: '/llms.txt' },
  { label: 'llms-full.txt', href: '/llms-full.txt' },
  { label: 'OpenAPI Spec', href: '/api/v1/docs/openapi' },
]

const companyLinks: FooterLinkItem[] = [
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
]

const legalLinks: FooterLinkItem[] = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
  { label: 'Security', href: '/security' },
]

// URLs that should NOT be treated as Inertia visits — they serve raw
// files (txt / JSON / YAML) from controllers that don't include the
// InertiaRails::Controller concern. Using Inertia's <Link> for these
// adds an X-Inertia header that crashes the middleware on response.
function isNonInertiaHref(href: string): boolean {
  if (href.startsWith('/api/')) return true
  if (/\.(txt|json|ya?ml|xml|pdf|csv|md)$/i.test(href)) return true
  return false
}

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  const { url } = usePage()
  const isHome = url === '/'
  const className = 'text-sm text-muted-foreground hover:text-foreground transition-colors'

  if (href.startsWith('/#')) {
    const hash = href.slice(1)
    return (
      <a
        href={href}
        onClick={(e) => {
          if (isHome) {
            e.preventDefault()
            document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' })
          }
        }}
        className={className}
      >
        {children}
      </a>
    )
  }

  if (isNonInertiaHref(href)) {
    return (
      <a href={href} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    )
  }

  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  )
}

function FooterColumn({ title, links }: { title: string; links: FooterLinkItem[] }) {
  return (
    <div>
      <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground/80 mb-5">
        {title}
      </h3>
      <ul className="space-y-3">
        {links.map((link) => (
          <li key={link.label}>
            <FooterLink href={link.href}>{link.label}</FooterLink>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Footer() {
  return (
    <footer className="relative isolate overflow-hidden">
      {/* Sunken surface, no top border */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'linear-gradient(180deg, transparent 0%, hsl(var(--surface-sunken)) 6%)',
        }}
      />

      {/* Massive wordmark across the top — quiet, single colour */}
      <div className="relative mx-auto max-w-7xl px-6 pt-20 lg:px-8">
        <Link href="/" className="block">
          <span className="block font-heading text-[clamp(4rem,15vw,12rem)] font-bold leading-[0.85] tracking-tighter text-foreground/10 hover:text-foreground/20 transition-colors">
            KonexZero
          </span>
        </Link>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-5">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2">
              <LogoIcon />
              <span className="font-heading text-base font-semibold tracking-tight">KonexZero</span>
            </div>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
              Realtime voice, video, and screen sharing APIs powered by a global edge network.
            </p>

            {/* Live status pill */}
            <Link
              href="/status"
              className="mt-6 inline-flex items-center gap-2 rounded-md border border-foreground/10 bg-background/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:border-foreground/20 hover:text-foreground transition"
            >
              <span className="live-dot" />
              all systems · operational
            </Link>
          </div>

          <nav aria-label="Footer navigation" className="grid gap-8 sm:col-span-1 sm:grid-cols-2 lg:col-span-4 lg:grid-cols-4">
            <FooterColumn title="Product" links={productLinks} />
            <FooterColumn title="Developers" links={developerLinks} />
            <FooterColumn title="Company" links={companyLinks} />
            <FooterColumn title="Legal" links={legalLinks} />
          </nav>
        </div>
      </div>

      {/* Closer band — soft surface change instead of a hairline */}
      <div
        className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-6 py-6 sm:flex-row sm:items-center lg:px-8"
        style={{ background: 'transparent' }}
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70">
          © {new Date().getFullYear()} KonexZero · All rights reserved.
        </p>
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70">
          <span>A product of</span>
          <FuturaLogo size="sm" />
        </div>
      </div>
    </footer>
  )
}
